import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getIn } from 'formik';
import { Select as AntdSelect } from 'antd';
import { withTooltip } from '../../../hocs/withTooltip';
import { CustomField } from '../CustomField/CustomField';

import styles from './index.module.css';
import { renderVehicleTypeWithUberBadge } from '../../common/VehicleTypeRenderer/VehicleTypeRenderer';

const { Option } = AntdSelect;

const CustomSelect = ({
  value,
  field,
  onChange,
  options,
  placeholder,
  form,
  mode,
  maxTagCount,
  maxTagTextLength,
  disabled,
  tagRender,
  showSearch,
  allowClear,
  suffixIcon,
  myClassName,
  whenFieldDisabledShowTooltip,
  getPopupContainer,
  myDropdownClassName,
}) => {
  const handleChange = (value) => {
    if (onChange) {
      onChange({
        name: field.name,
        value,
        setFieldValue: form.setFieldValue,
        setValues: form.setValues,
        values: form.values,
        validateForm: form.validateForm,
      });
    } else {
      form.setFieldValue(field.name, value);
    }
  };

  const onFilter = useCallback(
    (input, option) =>
      option.props.value.toLowerCase().startsWith(input.toLowerCase()) ||
      option.children.toLowerCase().startsWith(input.toLowerCase()),
    [],
  );

  const popUpContainer = (trigger) => {
    if (getPopupContainer) return getPopupContainer(trigger);
    else return trigger.parentElement;
  };

  return withTooltip(
    <AntdSelect
      allowClear={allowClear && value}
      filterOption={onFilter}
      showSearch={showSearch}
      name={field.name}
      value={value || field.value || null}
      onChange={handleChange}
      mode={mode}
      maxTagCount={maxTagCount}
      maxTagTextLength={maxTagTextLength}
      onBlur={() => form.setTouched({ ...form.touched, [field.name]: true })}
      disabled={disabled}
      placeholder={placeholder}
      tagRender={tagRender}
      suffixIcon={suffixIcon}
      className={classnames({
        [styles.select]: true,
        [styles.customSelect]: true,
        [styles.allowClear]: allowClear,
        [styles.error]: !disabled && !getIn(form.values, field.name) && getIn(form.errors, field.name),
        [styles.error]:
          !disabled && (getIn(form.touched, field.name) || form.touched[field.name]) && getIn(form.errors, field.name),
        [myClassName]: myClassName,
      })}
      popupClassName={classnames({
        [styles.dropdown]: true,
        [myDropdownClassName]: myDropdownClassName,
      })}
      getPopupContainer={popUpContainer}
      data-testid='select'>
      {options && field.name === 'vehicleType' ? (() => {
        const uberOptions = options.filter((o) => o.value === 'uberAmbulatoryRide');
        const nonUberOptions = options.filter((o) => o.value !== 'uberAmbulatoryRide');
        const hasUberOption = uberOptions.length > 0;

        return (
          <>
            {uberOptions.map((option) => (
              <Option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={classnames(styles.vehicleTypeOption, styles.uberOption)}
              >
                <div className={styles.vehicleTypeOptionContent}>
                  {renderVehicleTypeWithUberBadge(option.value)}
                </div>
              </Option>
            ))}

            {hasUberOption && nonUberOptions.length > 0 && (
              <>
                <Option
                  key="medhaulGroup"
                  value="medhaulGroup"
                  disabled
                  className={styles.medhaulGroupLabel}
                >
                  Fulfilled by MedHaul Network
                </Option>
                {nonUberOptions.map((option) => (
                  <Option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={styles.vehicleTypeOption}
                  >
                    <div className={styles.vehicleTypeOptionContent}>
                      {option.title}
                    </div>
                  </Option>
                ))}
              </>
            )}

            {!hasUberOption &&
              nonUberOptions.map((option) => (
                <Option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={styles.vehicleTypeOption}
                >
                  <div className={styles.vehicleTypeOptionContent}>
                    {option.title}
                  </div>
                </Option>
              ))}
          </>
        );
      })() : options ? (
        options.map((option) => (
          <Option key={option.value} value={option.value} disabled={option.disabled}>
            {option.value === 'uberAmbulatoryRide'
              ? renderVehicleTypeWithUberBadge(option.value)
              : option.title}
          </Option>
        ))
      ) : null}
    </AntdSelect>,
    whenFieldDisabledShowTooltip,
  );
};

export const Select = ({
  options,
  name,
  placeholder,
  value,
  onChange,
  customErrorMessage,
  mode,
  maxTagCount,
  maxTagTextLength,
  disabled,
  tagRender,
  showSearch,
  suffixIcon,
  allowClear,
  myClassName,
  whenFieldDisabledShowTooltip,
  fastField,
  getPopupContainer,
  myDropdownClassName,
}) => {
  return (
    <CustomField fastField={fastField} name={name} options={options} placeholder={placeholder} onChange={onChange}>
      {({ form, field }) => {
        return (
          <>
            <CustomSelect
              form={form}
              field={field}
              options={options}
              placeholder={placeholder}
              onChange={onChange}
              value={value}
              mode={mode}
              maxTagCount={maxTagCount}
              maxTagTextLength={maxTagTextLength}
              disabled={disabled}
              tagRender={tagRender}
              showSearch={showSearch}
              allowClear={allowClear}
              suffixIcon={suffixIcon}
              myClassName={myClassName}
              whenFieldDisabledShowTooltip={whenFieldDisabledShowTooltip}
              getPopupContainer={getPopupContainer}
              myDropdownClassName={myDropdownClassName}
            />
            {!disabled &&
              !customErrorMessage &&
              (getIn(form.touched, field.name) || form.touched[field.name]) &&
              getIn(form.errors, field.name) && (
                <div className={styles.errorMessage} role='alert'>
                  {getIn(form.errors, field.name)}
                </div>
              )}
          </>
        );
      }}
    </CustomField>
  );
};

Select.propTypes = {
  options: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func,
  customErrorMessage: PropTypes.bool,
  allowClear: PropTypes.bool,
  filterOption: PropTypes.func,
  suffixIcon: PropTypes.node,
  myClassName: PropTypes.string,
  whenFieldDisabledShowTooltip: PropTypes.bool,
  myDropdownClassName: PropTypes.string,
};
